import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useApp } from '../context/AppContext';
import { t } from '../i18n/translations';

const s = {
  page: { maxWidth: 960, margin: '0 auto', padding: '32px 16px' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 36 },
  statCard: { background: 'var(--surface)', borderRadius: 14, padding: '20px 24px', boxShadow: 'var(--shadow)', display: 'flex', flexDirection: 'column', gap: 6 },
  statNum: { fontSize: 32, fontWeight: 800 },
  statLabel: { fontSize: 13, fontWeight: 600, color: 'var(--text3)' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  sectionTitle: { fontSize: 22, fontWeight: 700, color: 'var(--heading)' },
  sectionDivider: { marginBottom: 40 },
  btn: { background: 'var(--primary)', color: '#fff', border: 'none', padding: '11px 22px', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 },
  card: { background: 'var(--surface)', borderRadius: 16, padding: 24, boxShadow: 'var(--shadow)' },
  cardTop: { cursor: 'pointer' },
  avatarWrap: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 },
  avatar: { width: 54, height: 54, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border)', flexShrink: 0 },
  avatarInitials: { width: 54, height: 54, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#fff', flexShrink: 0 },
  cardName: { fontSize: 18, fontWeight: 700, color: 'var(--heading)', marginBottom: 4 },
  cardSub: { color: 'var(--text3)', fontSize: 14, marginBottom: 4 },
  badge: { display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  badgeGreen: { display: 'inline-block', background: 'var(--success-bg)', color: 'var(--success-text)', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  cardActions: { display: 'flex', gap: 8, marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 },
  editBtn: { flex: 1, background: 'var(--surface2)', color: 'var(--text3)', border: 'none', padding: '7px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  delBtn: { flex: 1, background: 'var(--error-bg)', color: 'var(--error-text)', border: 'none', padding: '7px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modalCard: { background: 'var(--surface)', padding: 36, borderRadius: 16, width: '100%', maxWidth: 440, maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: 20, fontWeight: 700, marginBottom: 24, color: 'var(--heading)' },
  label: { display: 'block', marginBottom: 6, fontWeight: 600, color: 'var(--text2)', fontSize: 14 },
  input: { width: '100%', padding: '11px 13px', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: 15, marginBottom: 16, boxSizing: 'border-box', background: 'var(--surface2)', color: 'var(--text)' },
  row: { display: 'flex', gap: 12, marginTop: 8 },
  cancelBtn: { flex: 1, padding: 12, border: '1.5px solid var(--border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', fontWeight: 600 },
  saveBtn: { flex: 1, padding: 12, background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  empty: { textAlign: 'center', padding: 40, color: 'var(--text4)', background: 'var(--surface2)', borderRadius: 12 },
  errorMsg: { color: 'var(--error-text)', fontSize: 14, marginBottom: 12 },
  photoWrap: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 },
  photoPreview: { width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border)' },
  photoInitials: { width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, color: '#fff' },
  uploadBtn: { background: 'var(--primary-light)', color: 'var(--primary)', border: '1.5px solid var(--primary-border)', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  uploadNote: { color: 'var(--text4)', fontSize: 12, marginTop: 4 }
};

const emptySeniorForm = { name: '', phone: '', date_of_birth: '', notes: '', photo: '' };
const emptyCaregiverForm = { name: '', phone: '', date_of_birth: '', relationship: '', notes: '', photo: '' };

const SENIOR_COLORS = ['#2563eb', '#7c3aed', '#0891b2', '#0f766e'];
const CAREGIVER_COLORS = ['#16a34a', '#b45309', '#be123c', '#1d4ed8'];

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function AvatarOrInitials({ photo, name, size = 54, colors = SENIOR_COLORS, index = 0 }) {
  const bg = colors[index % colors.length];
  if (photo) {
    return <img src={`${photo}`} alt={name} style={{ ...s.avatar, width: size, height: size }} />;
  }
  return (
    <div style={{ ...s.avatarInitials, width: size, height: size, background: bg, fontSize: size * 0.35 }}>
      {getInitials(name)}
    </div>
  );
}

export default function Dashboard() {
  const { language } = useApp();
  const T = (key) => t(language, key);
  const [seniors, setSeniors] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [stats, setStats] = useState({ todayCount: 0, upcomingCount: 0 });
  const [modal, setModal] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [seniorForm, setSeniorForm] = useState(emptySeniorForm);
  const [caregiverForm, setCaregiverForm] = useState(emptyCaregiverForm);
  const [formError, setFormError] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    const [sr, cr, st] = await Promise.allSettled([
      api.get('/seniors'),
      api.get('/caregivers'),
      api.get('/appointments/stats/today')
    ]);
    if (sr.status === 'fulfilled') setSeniors(sr.value.data);
    if (cr.status === 'fulfilled') setCaregivers(cr.value.data);
    if (st.status === 'fulfilled') setStats(st.value.data);
  };

  const openAdd = (type) => { setEditTarget(null); setFormError(''); setModal(type); };

  const openEdit = (e, type, item) => {
    e.stopPropagation();
    setEditTarget(item);
    setFormError('');
    if (type === 'senior') setSeniorForm({ name: item.name, phone: item.phone || '', date_of_birth: item.date_of_birth || '', notes: item.notes || '', photo: item.photo || '' });
    else setCaregiverForm({ name: item.name, phone: item.phone || '', date_of_birth: item.date_of_birth || '', relationship: item.relationship || '', notes: item.notes || '', photo: item.photo || '' });
    setModal(type);
  };

  const closeModal = () => { setModal(null); setEditTarget(null); setFormError(''); setSeniorForm(emptySeniorForm); setCaregiverForm(emptyCaregiverForm); };

  const uploadPhoto = async (file, type) => {
    setUploadingPhoto(true);
    try {
      const data = new FormData();
      data.append('photo', file);
      const res = await api.post('/upload/photo', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (type === 'senior') setSeniorForm(f => ({ ...f, photo: res.data.url }));
      else setCaregiverForm(f => ({ ...f, photo: res.data.url }));
    } catch (err) {
      setFormError(err.response?.data?.error || 'Photo upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const submitSenior = async (e) => {
    e.preventDefault();
    setFormError('');
    try {
      if (editTarget) await api.put(`/seniors/${editTarget._id}`, seniorForm);
      else await api.post('/seniors', seniorForm);
      closeModal();
      fetchAll();
    } catch (err) { setFormError(err.response?.data?.error || 'Something went wrong'); }
  };

  const submitCaregiver = async (e) => {
    e.preventDefault();
    setFormError('');
    try {
      if (editTarget) await api.put(`/caregivers/${editTarget._id}`, caregiverForm);
      else await api.post('/caregivers', caregiverForm);
      closeModal();
      fetchAll();
    } catch (err) { setFormError(err.response?.data?.error || 'Something went wrong'); }
  };

  const deleteSenior = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this senior? Their medications will also be removed.')) return;
    try {
      await api.delete(`/seniors/${id}`);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete senior. Please try again.');
    }
  };

  const deleteCaregiver = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this caregiver?')) return;
    try {
      await api.delete(`/caregivers/${id}`);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete caregiver. Please try again.');
    }
  };

  const getAge = (dob) => {
    if (!dob) return '';
    return Math.floor((Date.now() - new Date(dob).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
  };

  const PhotoUploader = ({ type, form }) => (
    <div style={s.photoWrap}>
      {form.photo
        ? <img src={`${form.photo}`} alt="Preview" style={s.photoPreview} />
        : <div style={{ ...s.photoInitials, background: type === 'senior' ? '#2563eb' : '#16a34a' }}>{getInitials(form.name)}</div>
      }
      <div>
        <label style={{ ...s.uploadBtn, display: 'inline-block', cursor: uploadingPhoto ? 'not-allowed' : 'pointer', opacity: uploadingPhoto ? 0.7 : 1 }}>
          {uploadingPhoto ? 'Uploading...' : form.photo ? 'Change Photo' : 'Upload Photo'}
          <input type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => e.target.files[0] && uploadPhoto(e.target.files[0], type)}
            disabled={uploadingPhoto} />
        </label>
        <div style={s.uploadNote}>JPG, PNG, GIF up to 2MB</div>
        {form.photo && (
          <button type="button" style={{ ...s.uploadBtn, background: '#fee2e2', color: '#dc2626', border: '1.5px solid #fca5a5', marginTop: 4 }}
            onClick={() => type === 'senior' ? setSeniorForm(f => ({ ...f, photo: '' })) : setCaregiverForm(f => ({ ...f, photo: '' }))}>
            Remove
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={s.page} className="page-wrap">

      {/* Stats Row */}
      <div style={s.statsRow} className="stats-row">
        <div style={s.statCard} className="stat-card surface">
          <div style={{ ...s.statNum, color: 'var(--primary)' }}>{seniors.length}</div>
          <div style={s.statLabel}>👴 {T('totalSeniors')}</div>
        </div>
        <div style={s.statCard} className="stat-card surface">
          <div style={{ ...s.statNum, color: '#16a34a' }}>{caregivers.length}</div>
          <div style={s.statLabel}>👩‍⚕️ {T('totalCaregivers')}</div>
        </div>
        <div style={s.statCard} className="stat-card surface">
          <div style={{ ...s.statNum, color: '#f59e0b' }}>{stats.todayCount}</div>
          <div style={s.statLabel}>📅 {T('todayAppointments')}</div>
        </div>
        <div style={s.statCard} className="stat-card surface">
          <div style={{ ...s.statNum, color: '#7c3aed' }}>{stats.upcomingCount}</div>
          <div style={s.statLabel}>🔔 {T('upcoming')}</div>
        </div>
      </div>

      {/* Seniors Section */}
      <div style={s.sectionDivider}>
        <div style={s.sectionHeader}>
          <div style={s.sectionTitle} className="big-title">{T('seniors')} <span style={{ color: 'var(--text4)', fontSize: 16 }}>({seniors.length})</span></div>
          <button style={s.btn} onClick={() => openAdd('senior')}>+ {T('addSenior')}</button>
        </div>
        {seniors.length === 0 ? (
          <div style={s.empty}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>👴</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>No seniors added yet</div>
            <div style={{ fontSize: 14 }}>Click "Add Senior" to get started</div>
          </div>
        ) : (
          <div style={s.grid} className="card-grid">
            {seniors.map((item, idx) => (
              <div key={item._id} style={s.card} className="card surface">
                <div style={s.cardTop} onClick={() => navigate(`/senior/${item._id}`)}>
                  <div style={s.avatarWrap}>
                    <AvatarOrInitials photo={item.photo} name={item.name} size={54} colors={SENIOR_COLORS} index={idx} />
                    <div>
                      <div style={s.cardName}>{item.name}</div>
                      {item.date_of_birth && <div style={s.cardSub}>{T('age')}: {getAge(item.date_of_birth)} yrs</div>}
                      {item.phone && <div style={s.cardSub}>{item.phone}</div>}
                    </div>
                  </div>
                  <span style={s.badge}>View Medications →</span>
                </div>
                <div style={s.cardActions} className="btn-group">
                  <button style={s.editBtn} onClick={(e) => openEdit(e, 'senior', item)}>{T('edit')}</button>
                  <button style={s.delBtn} onClick={(e) => deleteSenior(e, item._id)}>{T('delete')}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Caregivers Section */}
      <div style={s.sectionDivider}>
        <div style={s.sectionHeader}>
          <div style={s.sectionTitle} className="big-title">{T('caregivers')} <span style={{ color: 'var(--text4)', fontSize: 16 }}>({caregivers.length})</span></div>
          <button style={{ ...s.btn, background: '#16a34a' }} onClick={() => openAdd('caregiver')}>+ {T('addCaregiver')}</button>
        </div>
        {caregivers.length === 0 ? (
          <div style={s.empty}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>👩‍⚕️</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>No caregivers added yet</div>
            <div style={{ fontSize: 14 }}>Click "Add Caregiver" to add family members or helpers</div>
          </div>
        ) : (
          <div style={s.grid} className="card-grid">
            {caregivers.map((item, idx) => (
              <div key={item._id} style={s.card} className="card surface">
                <div style={{ ...s.cardTop }} onClick={() => navigate(`/caregiver/${item._id}`)}>
                  <div style={s.avatarWrap}>
                    <AvatarOrInitials photo={item.photo} name={item.name} size={54} colors={CAREGIVER_COLORS} index={idx} />
                    <div>
                      <div style={s.cardName}>{item.name}</div>
                      {item.relationship && <div style={s.cardSub}>{item.relationship}</div>}
                      {item.date_of_birth && <div style={s.cardSub}>{T('age')}: {getAge(item.date_of_birth)} yrs</div>}
                      {item.phone && <div style={s.cardSub}>{item.phone}</div>}
                    </div>
                  </div>
                  <span style={s.badgeGreen}>View Appointments →</span>
                </div>
                <div style={s.cardActions} className="btn-group">
                  <button style={s.editBtn} onClick={(e) => openEdit(e, 'caregiver', item)}>{T('edit')}</button>
                  <button style={s.delBtn} onClick={(e) => deleteCaregiver(e, item._id)}>{T('delete')}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Senior Modal */}
      {modal === 'senior' && (
        <div style={s.modal}>
          <div style={s.modalCard} className="modal-inner">
            <div style={s.modalTitle}>{editTarget ? 'Edit Senior' : T('addSenior')}</div>
            <form onSubmit={submitSenior}>
              {formError && <div style={s.errorMsg}>{formError}</div>}
              <label style={s.label}>{T('photo')}</label>
              <PhotoUploader type="senior" form={seniorForm} />
              <label style={s.label}>{T('fullName')} *</label>
              <input style={s.input} value={seniorForm.name} onChange={e => setSeniorForm({ ...seniorForm, name: e.target.value })} required />
              <label style={s.label}>{T('phone')}</label>
              <input style={s.input} value={seniorForm.phone} onChange={e => setSeniorForm({ ...seniorForm, phone: e.target.value })} placeholder="+1234567890" />
              <label style={s.label}>Date of Birth *</label>
              <input style={s.input} type="date" value={seniorForm.date_of_birth} onChange={e => setSeniorForm({ ...seniorForm, date_of_birth: e.target.value })} required />
              <label style={s.label}>{T('notes')}</label>
              <input style={s.input} value={seniorForm.notes} onChange={e => setSeniorForm({ ...seniorForm, notes: e.target.value })} />
              <div style={s.row}>
                <button type="button" style={s.cancelBtn} onClick={closeModal}>{T('cancel')}</button>
                <button type="submit" style={s.saveBtn}>{editTarget ? T('save') : T('save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Caregiver Modal */}
      {modal === 'caregiver' && (
        <div style={s.modal}>
          <div style={s.modalCard} className="modal-inner">
            <div style={s.modalTitle}>{editTarget ? 'Edit Caregiver' : T('addCaregiver')}</div>
            <form onSubmit={submitCaregiver}>
              {formError && <div style={s.errorMsg}>{formError}</div>}
              <label style={s.label}>{T('photo')}</label>
              <PhotoUploader type="caregiver" form={caregiverForm} />
              <label style={s.label}>{T('fullName')} *</label>
              <input style={s.input} value={caregiverForm.name} onChange={e => setCaregiverForm({ ...caregiverForm, name: e.target.value })} required />
              <label style={s.label}>Relationship</label>
              <select style={s.input} value={caregiverForm.relationship} onChange={e => setCaregiverForm({ ...caregiverForm, relationship: e.target.value })}>
                <option value="">Select relationship...</option>
                <option>Son</option><option>Daughter</option><option>Spouse</option>
                <option>Brother</option><option>Sister</option><option>Nurse</option>
                <option>Doctor</option><option>Friend</option><option>Other</option>
              </select>
              <label style={s.label}>{T('phone')}</label>
              <input style={s.input} value={caregiverForm.phone} onChange={e => setCaregiverForm({ ...caregiverForm, phone: e.target.value })} placeholder="+1234567890" />
              <label style={s.label}>Date of Birth *</label>
              <input style={s.input} type="date" value={caregiverForm.date_of_birth} onChange={e => setCaregiverForm({ ...caregiverForm, date_of_birth: e.target.value })} required />
              <label style={s.label}>{T('notes')}</label>
              <input style={s.input} value={caregiverForm.notes} onChange={e => setCaregiverForm({ ...caregiverForm, notes: e.target.value })} />
              <div style={s.row}>
                <button type="button" style={s.cancelBtn} onClick={closeModal}>{T('cancel')}</button>
                <button type="submit" style={{ ...s.saveBtn, background: '#16a34a' }}>{editTarget ? T('save') : T('save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
